import {
  UserFragment,
  useDeleteUserMutation,
  useUsersQuery,
} from "@graphql/users/users.g";
import {
  type ColumnDef,
  OnChangeFn,
  PaginationState,
  SortingState,
  createColumnHelper,
} from "@tanstack/react-table";
import { useDebounce } from "@uidotdev/usehooks";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router";

import { useTableColumnSettingsProvider } from "@providers/TableColumnSettingsProvider/useTableColumnSettingsProvider";

import { Box } from "@design-system/Box/Box";
import { Dropdown } from "@design-system/Dropdown/Dropdown";
import { IconButton } from "@design-system/IconButton/IconButton";
import { Table } from "@design-system/Table/Table";

import { routes } from "@pages/routes";

import { usePaginationWithParams } from "@utils/hooks/usePaginationParams";
import { ConfigurableTableSystem } from "@utils/table/TableConfigurationSystem";

import {
  UsersTableConfig,
  renderName,
  renderEmail,
  renderCreatedAt,
} from "./UsersTableConfig";
import { UserTableColumns } from "./UI/UsersTableColumns";

const PAGE_SIZE = 30;
const SEARCH_DEBOUNCE_TIME = 400;

const columnHelper = createColumnHelper<UserFragment>();

type Props = {
  initialPageIndex?: number;
  nameSearch?: string;
  emailSearch?: string;
  showFooter?: boolean;
  showHeader?: boolean;
  showPageSizeDropdown?: boolean;
  pageSize?: number;
  sorting?: SortingState;
  onSortingChange?: (sorting: SortingState) => void;
};

export const UsersTable = ({
  initialPageIndex = 0,
  nameSearch,
  emailSearch,
  showFooter = true,
  showHeader = true,
  showPageSizeDropdown = true,
  pageSize = PAGE_SIZE,
  sorting: controlledSorting,
  onSortingChange,
}: Props) => {
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: initialPageIndex,
    pageSize: pageSize,
  });
  const [internalSorting, setInternalSorting] = useState<SortingState>([]);
  const sorting = controlledSorting ?? internalSorting;
  const handleSortingChange = useCallback<OnChangeFn<SortingState>>(
    (updater) => {
      const newSorting =
        typeof updater === "function" ? updater(sorting) : updater;
      if (onSortingChange) {
        onSortingChange(newSorting);
      } else {
        setInternalSorting(newSorting);
      }
    },
    [sorting, onSortingChange],
  );

  const { columnSettings, columnOrder } = useTableColumnSettingsProvider();

  const debouncedNameSearch = useDebounce(nameSearch, SEARCH_DEBOUNCE_TIME);
  const debouncedEmailSearch = useDebounce(emailSearch, SEARCH_DEBOUNCE_TIME);
  const usersQuery = useUsersQuery({
    first: pagination.pageSize,
    offset: pagination.pageIndex * pagination.pageSize,
    // Separate search parameters for name and email
    name_Icontains: debouncedNameSearch || undefined,
    email_Icontains: debouncedEmailSearch || undefined,
    sortBy: ConfigurableTableSystem.generateSortParameter(
      UsersTableConfig.columns,
      sorting,
    ),
  });
  const deleteUserMutation = useDeleteUserMutation();
  const navigate = useNavigate();
  const handlePaginationChange = usePaginationWithParams(
    pagination,
    setPagination,
  );

  const deleteUser = deleteUserMutation.mutateAsync;
  const refetchUsers = usersQuery.refetch;

  const totalCount = usersQuery.data?.users?.totalCount ?? undefined;

  useEffect(() => {
    setPagination({ pageIndex: 0, pageSize: pageSize });
  }, [debouncedNameSearch, debouncedEmailSearch, pageSize]);

  const data = useMemo(() => {
    return (
      usersQuery.data?.users?.edges
        .map((edge) => edge?.node)
        .filter((user) => !!user) ?? []
    );
  }, [usersQuery.data]);

  const handleDetailsClick = useCallback(
    (userId: string) => {
      navigate(
        routes.portal.users.user.$buildPath({
          params: { userId },
        }),
        {
          viewTransition: true,
        },
      );
    },
    [navigate],
  );

  const handleEditClick = useCallback(
    (userId: string) => {
      navigate(routes.portal.users.user.$buildPath({ params: { userId } }));
    },
    [navigate],
  );

  const handleDeleteClick = useCallback(
    async (userId: string) => {
      await deleteUser({ userId });
      refetchUsers();
    },
    [deleteUser, refetchUsers],
  );

  const configuredColumns = useMemo(() => {
    // Create column configuration with custom renderers
    const configWithRenderers = {
      ...UsersTableConfig,
      columns: Object.fromEntries(
        Object.entries(UsersTableConfig.columns).map(
          ([key, config]) => [
            key,
            {
              ...config,
              render: (value: unknown, row: UserFragment) => {
                const columnKey = key as UserTableColumns;

                // Map to the appropriate render function
                switch (columnKey) {
                  case UserTableColumns.name:
                    return renderName(value, row, handleDetailsClick);
                  case UserTableColumns.email:
                    return renderEmail(value);
                  case UserTableColumns.createdAt:
                    return renderCreatedAt(value);
                  default:
                    return String(value);
                }
              },
            },
          ],
        ),
      ) as typeof UsersTableConfig.columns,
    };

    return ConfigurableTableSystem.generateColumnsFromConfig(
      data,
      configWithRenderers,
      {
        columnOrder:
          columnOrder.length > 0
            ? (columnOrder as UserTableColumns[])
            : undefined,
        columnVisibility: columnSettings,
      },
    );
  }, [data, columnOrder, columnSettings, handleDetailsClick]);

  const allColumnsWithActions = useMemo(() => {
    const columns: ColumnDef<UserFragment, unknown>[] = [
      ...configuredColumns,
    ];

    // Always add the actions column at the end
    columns.push(
      columnHelper.display({
        id: "actions",
        header: "",
        meta: {
          sticky: true,
          actions: true,
        },
        cell: (props) => {
          return (
            <Box gap={2}>
              <Dropdown
                trigger={<IconButton size="sm" icon="faEllipsisVertical" />}
              >
                <Dropdown.Option
                  label="Details"
                  icon="faFileLines"
                  onSelect={() => handleDetailsClick(props.row.original.id)}
                />
                <Dropdown.Option
                  label="Edit"
                  icon="faPencil"
                  onSelect={() => handleEditClick(props.row.original.id)}
                />
                <Dropdown.Option
                  label="Delete"
                  icon="faTrash"
                  onSelect={() => handleDeleteClick(props.row.original.id)}
                />
              </Dropdown>
            </Box>
          );
        },
      }),
    );

    return columns;
  }, [configuredColumns, handleDetailsClick, handleEditClick, handleDeleteClick]);

  const handlePageSizeChange = useCallback((pageSize: number) => {
    setPagination({ pageIndex: 0, pageSize });
  }, []);

  return (
    <Table<UserFragment, string>
      columns={allColumnsWithActions as ColumnDef<UserFragment, string>[]}
      data={data}
      rowCount={totalCount}
      onPaginationChange={handlePaginationChange}
      pagination={pagination}
      isLoading={usersQuery.isLoading}
      isFetching={usersQuery.isFetching}
      onPageSizeChange={handlePageSizeChange}
      sorting={sorting}
      onSortingChange={handleSortingChange}
      showFooter={showFooter}
      showHeader={showHeader}
      showPageSizeDropdown={showPageSizeDropdown}
      columnVisibility={columnSettings}
    />
  );
};
